import { useProduct } from "@/components/pdp/PdpDataContext";
import { Facebook, Instagram, ArrowRight } from "lucide-react";

const COLS = [
  {
    heading: "Shop",
    links: ["Shop All", "Best-sellers", "Sale", "Sets & Bundles", "Anti-Aging", "Redness Control", "Dryness", "Reviews"],
  },
  {
    heading: "Customer care",
    links: ["About us", "FAQ", "Customer Support", "Find a Store", "Become a Retailer", "Become an Affiliate", "Privacy Policy", "Terms of Service", "Refund Policy"],
  },
];

// HP3 splits the two long lists into three tighter, best-practice columns
// (Shop / Company / Support) drawn from Riversol's real menu.
const HP3_COLS = [
  {
    heading: "Shop",
    links: ["Shop All", "Best-sellers", "Sale", "Sets & Bundles", "Anti-Aging", "Redness Control", "Dryness"],
  },
  {
    heading: "Company",
    links: ["About us", "Reviews", "Find a Store", "Become a Retailer", "Become an Affiliate"],
  },
  {
    heading: "Support",
    links: ["FAQ", "Customer Support", "Privacy Policy", "Terms of Service", "Refund Policy"],
  },
];

export const RiversolFooter = ({ hp3 = false, dark = false }) => {
  const { logo, v31, v32 } = useProduct();

  // HP3 — a distinct, editorial footer: deep sage/blue-green field, three
  // tight menu columns on the left, a wider newsletter column pushed to the
  // far right, and an oversized Riversol wordmark spanning the full width
  // (OneSkin-style) beneath it.
  if (hp3) {
    return (
      <footer className="bg-[#0F2E2E] text-[#D9E2DA]" data-testid="riversol-footer">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-2 lg:grid-cols-12 gap-y-12 gap-x-8">
            {HP3_COLS.map((col, idx) => (
              <div key={col.heading} className={`lg:col-span-2 ${idx === 0 ? "lg:col-start-1" : ""}`}>
                <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white mb-5">{col.heading}</h4>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l}>
                      <a href="https://www.riversol.com" className="text-sm text-[#A9BDAE] hover:text-white transition-colors">{l}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            {/* Newsletter — last column, wider than the menus, pinned right. */}
            <div className="col-span-2 lg:col-span-4 lg:col-start-9">
              <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white mb-5">Stay in touch</h4>
              <p className="text-sm leading-relaxed text-[#A9BDAE] max-w-sm mb-6">
                Join Riversol today and stay up to date on the latest promotions, launches and dermatologist skincare tips.
              </p>
              <div className="flex items-center gap-2 max-w-md border-b border-white/25 pb-2">
                <input
                  type="email"
                  placeholder="Email address"
                  data-testid="footer-newsletter-input"
                  className="bg-transparent text-sm text-white placeholder:text-white/40 flex-1 outline-none py-1"
                />
                <button data-testid="footer-newsletter-submit" aria-label="Subscribe" className="text-white hover:translate-x-1 transition-transform">
                  <ArrowRight size={18} />
                </button>
              </div>
              <div className="flex items-center gap-4 mt-8">
                <a href="https://www.facebook.com/RiversolMD" aria-label="Facebook" className="text-[#A9BDAE] hover:text-white transition-colors"><Facebook size={18} /></a>
                <a href="https://www.instagram.com/riversolskincare/" aria-label="Instagram" className="text-[#A9BDAE] hover:text-white transition-colors"><Instagram size={18} /></a>
              </div>
            </div>
          </div>

          {/* Oversized wordmark filling the width under the menus + email. */}
          <div className="mt-14 lg:mt-20 border-t border-white/10 pt-10 lg:pt-14">
            <div
              className="font-semibold tracking-[-0.03em] leading-[0.8] text-white/90 select-none whitespace-nowrap overflow-hidden"
              style={{ fontSize: "clamp(3rem, 21.4vw, 26rem)" }}
              data-testid="footer-wordmark"
            >
              Riversol
            </div>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 flex items-center justify-between text-xs text-[#8CA394] flex-wrap gap-2">
            <span>© 2026 Riversol</span>
            <span className="font-mono tracking-[0.15em] uppercase">Homepage Concept · Desktop</span>
          </div>
        </div>
      </footer>
    );
  }

  // PDP3.2 ships the dark-blue (#003D4C) footer permanently. `dark` opts a
  // page (e.g. HP4) into a solid dark #003646 footer — same layout, no
  // inversion — while leaving the menu and logo untouched.
  const isDark = v32 || dark;
  const invertLogo = !v31 || isDark;
  return (
    <footer
      className={`${dark ? "bg-[#003646]" : "bg-riversol-blue"} text-white/80`}
      data-testid="riversol-footer"
      data-inverted-bg={isDark ? undefined : "blue"}
    >
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          {/* V3.1 uses the natural-colour Riversol wordmark (identical to
              the header) — no forced-white invert filter. V2/V3 keep the
              inverted white silhouette so it stays readable on the deep
              royal-blue footer they still ship with. */}
          <img
            src={logo}
            alt="Riversol"
            className="h-6 w-auto mb-6"
            style={invertLogo ? { filter: "brightness(0) invert(1)" } : undefined}
          />
          <p className="text-sm leading-relaxed max-w-xs mb-6">
            Join Riversol today and stay up to date on the latest promotions and sales.
          </p>
          <div className="flex items-center gap-2 max-w-sm border-b border-white/25 pb-2">
            <input
              type="email"
              placeholder="Email address"
              data-testid="footer-newsletter-input"
              className="bg-transparent text-sm text-white placeholder:text-white/40 flex-1 outline-none py-1"
            />
            <button data-testid="footer-newsletter-submit" aria-label="Subscribe" className="text-white hover:translate-x-1 transition-transform">
              <ArrowRight size={18} />
            </button>
          </div>
          <div className="flex items-center gap-4 mt-8">
            <a href="https://www.facebook.com/RiversolMD" aria-label="Facebook" className="hover:text-white transition-colors"><Facebook size={18} /></a>
            <a href="https://www.instagram.com/riversolskincare/" aria-label="Instagram" className="hover:text-white transition-colors"><Instagram size={18} /></a>
          </div>
        </div>
        {COLS.map((col) => (
          <div key={col.heading} className="lg:col-span-4">
            <h4 className="font-mono text-[11px] uppercase tracking-[0.2em] text-white mb-5">{col.heading}</h4>
            <ul className="space-y-3">
              {col.links.map((l) => (
                <li key={l}>
                  <a href="https://www.riversol.com" className="text-sm hover:text-white transition-colors">{l}</a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6 flex items-center justify-between text-xs text-white/50 flex-wrap gap-2">
          <span>© 2026 Riversol</span>
          <span className="font-mono tracking-[0.15em] uppercase">PDP Redesign Concept · Desktop</span>
        </div>
      </div>
    </footer>
  );
};

export default RiversolFooter;
